(function () {
  const modals = [];

  function initUpsellModal(modalEl) {
    const productsContainer = modalEl.querySelector('[data-upsell-products]');
    const recommendationsUrl = modalEl.dataset.recommendationsUrl;
    const productId = modalEl.dataset.productId;
    if (!productsContainer || !recommendationsUrl) return;

    let hasShown = false;

    // Mobile browsers often restore a page from the back-forward cache (bfcache)
    // when navigating back, which keeps this script's in-memory state (including
    // hasShown) exactly as it was before the user left the page. Reset it so a
    // fresh add-to-cart after returning is evaluated again instead of being
    // silently skipped.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) hasShown = false;
    });

    function fetchCartAndMaybeShow(addedQuantity) {
      if (hasShown) return;

      fetch(`${window.routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cart) => {
          const storageKey = 'upsellModalShown_' + cart.token + '_' + productId;

          // If the cart only contains what was just added, this is the start of a
          // new shopping trip (fresh cart after checkout, or cart was emptied) -
          // clear any stale flags for this cart token so the prompt can show again,
          // regardless of whether the checkout app rotated the cart token.
          if (typeof addedQuantity === 'number' && cart.item_count === addedQuantity) {
            Object.keys(localStorage)
              .filter((key) => key.indexOf('upsellModalShown_' + cart.token) === 0)
              .forEach((key) => localStorage.removeItem(key));
          }

          if (localStorage.getItem(storageKey)) return;

          fetch(recommendationsUrl)
            .then((response) => response.text())
            .then((text) => {
              const html = new DOMParser().parseFromString(text, 'text/html');
              const cards = html.querySelectorAll('[data-upsell-card]');
              if (!cards.length) return;

              productsContainer.innerHTML = '';
              cards.forEach((card) => productsContainer.appendChild(card));

              hasShown = true;
              localStorage.setItem(storageKey, '1');
              modalEl.show(document.activeElement || document.body);
            })
            .catch((error) => console.error('Upsell modal:', error));
        })
        .catch((error) => console.error('Upsell modal:', error));
    }

    productsContainer.addEventListener('change', function (event) {
      const select = event.target.closest('[data-upsell-variant-select]');
      if (!select) return;

      const card = select.closest('[data-upsell-card]');
      const option = select.options[select.selectedIndex];
      const addButton = card.querySelector('[data-upsell-add]');
      const priceEl = card.querySelector('[data-upsell-price]');

      if (addButton) {
        addButton.dataset.variantId = option.value;
        addButton.disabled = option.disabled;
        addButton.textContent = option.disabled ? 'Sold out' : 'Add to cart';
      }
      if (priceEl && option.dataset.price) {
        priceEl.textContent = option.dataset.price;
      }
    });

    productsContainer.addEventListener('click', function (event) {
      const addButton = event.target.closest('[data-upsell-add]');
      if (!addButton) return;
      event.preventDefault();
      if (addButton.disabled || addButton.getAttribute('aria-disabled') === 'true') return;

      const originalText = addButton.textContent;
      addButton.setAttribute('aria-disabled', 'true');
      addButton.textContent = 'Adding...';

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', addButton.dataset.variantId);
      formData.append('quantity', '1');
      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
      }
      config.body = formData;

      fetch(window.routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            console.error('Upsell modal:', response.description || response.message);
            addButton.textContent = originalText;
            addButton.removeAttribute('aria-disabled');
            return;
          }

          if (cart) cart.renderContents(response);
          addButton.textContent = 'Added';
          modalEl.hide();
        })
        .catch((error) => {
          console.error('Upsell modal:', error);
          addButton.textContent = originalText;
          addButton.removeAttribute('aria-disabled');
        });
    });

    modals.push(fetchCartAndMaybeShow);
  }

  function extractAddedQuantity(responseText) {
    try {
      const data = JSON.parse(responseText);
      // Shopify's /cart/add endpoint has two response shapes depending on the
      // request: a single-item add returns the line item with `quantity` at
      // the top level, while a multi-item add (items: [...]) returns
      // `{ items: [...] }` with quantity nested per item. Some apps (like
      // Zoorix's sticky bar) always use the multi-item shape even for a
      // single product, so both forms need to be handled.
      if (typeof data.quantity === 'number') return data.quantity;
      if (Array.isArray(data.items)) {
        return data.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      }
    } catch (e) {
      // ignore, treated as unknown below
    }
    return undefined;
  }

  function notifyModalsOfCartAdd(responseText) {
    const quantity = extractAddedQuantity(responseText);
    modals.forEach((fn) => fn(quantity));
  }

  // Any add-to-cart mechanism on the page - the theme's native button, a
  // third-party app's sticky bar, a quick-add widget, etc. - has to call
  // Shopify's real /cart/add endpoint to actually add an item, even if it
  // never touches the theme's own pubsub events. Watching the network layer
  // directly means this works no matter which button/app triggered the add.
  const cartAddPattern = /\/cart\/add(\.js)?(\?|$)/;

  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const method = (init && init.method) || (typeof input === 'object' && input.method) || 'GET';
    const isCartAdd = cartAddPattern.test(url) && String(method).toUpperCase() === 'POST';
    const promise = originalFetch.apply(this, arguments);
    if (isCartAdd) {
      promise
        .then((response) => {
          if (!response.ok) return;
          response
            .clone()
            .text()
            .then(notifyModalsOfCartAdd)
            .catch(() => {});
        })
        .catch(() => {});
    }
    return promise;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__isCartAdd = cartAddPattern.test(url) && String(method).toUpperCase() === 'POST';
    return originalOpen.apply(this, arguments);
  };

  const originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__isCartAdd) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) {
          notifyModalsOfCartAdd(this.responseText);
        }
      });
    }
    return originalSend.apply(this, arguments);
  };

  document.querySelectorAll('.upsell-modal').forEach(initUpsellModal);
})();
